import { MouseEvent, ReactNode, useEffect, useState } from 'react';
import MenuParams from 'shared/ui/menu-params';
import { Alert, TableRow, TableCell, IconButton, Chip, Paper, Stack, Box, Checkbox, FormControl, InputLabel, Select, MenuItem, Button, Typography } from '@mui/material';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import { canDeletePurchase, INVOICE_TYPE_CLIENT, INVOICE_TYPE_SUPPLIER, InvoiceDto, InvoiceRowDto, IPagingData, PurchaseInfoDto, PurchaseWccWarningDto } from '@sr/dto';
import { GridLayout, HeadCellsCollection } from 'shared/ui/grid-layout';
import { RowMenu, useMenu } from 'shared/ui/row-menu';
import { LinkToPurchase } from 'shared/ui/links/link-to-purchase';
import { LinkToInvoice } from 'entities/client-invoices-list/ui/link-to-invoice';
import { InvoiceStatusSpan } from 'entities/invoice/status-span/status-span';
import { LinkToLegalEntity } from 'shared/ui/links/link-to-legal-entity';
import PriceSpan from 'shared/ui/price-span';
import { InfoBanner } from 'shared/ui/info-banner';
import { LinkToMedia } from 'shared/ui/links/link-to-media';
import { LegendItem } from 'shared/ui/legend-list';
import { IRemotePagedCollection } from 'utils/remote-paged-collection.hook';
import { PurchasesFilter } from 'features/purchase/purchases-filter';
import { PURCHASE_STATUSES } from '@sr/dto';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import { useSnack } from 'shared/ui/snack';
import { patchMassPurchaseStatus } from 'shared/api/purchases';
import { formatShortDate } from 'utils/date-format-helpers';
import { Link } from 'shared/ui/link-base';

type Props = {
  collection: IRemotePagedCollection<PurchaseInfoDto>;
  paging: IPagingData;
  onChangePaging: (paging: IPagingData) => void;
  onDelete: (purchase: PurchaseInfoDto) => void;
};

export const PurchasesList = ({ collection, paging, onChangePaging, onDelete }: Props) => {
  const { openMenu, menuData } = useMenu<PurchaseInfoDto>({ deleteHandler: onDelete });
  const totals = collection.collection.meta?.[0];

  const [checkedItems, setCheckedItems] = useState<number[]>([]);

  const handleToggleAll = (e: any, checked: boolean) => {
    if (checked) setCheckedItems(collection.collection.items.map((x) => x.id));
    else setCheckedItems([]);
  };

  const isAllChecked = () => checkedItems.length !== collection.collection.items.length;

  const handleCheckChange = (id: number, checked: boolean) => {
    setCheckedItems(!checked ? checkedItems.filter((x) => x !== id) : [...checkedItems, id]);
  };

  const columns: HeadCellsCollection = [
    {
      label: <Checkbox onChange={handleToggleAll} value={!isAllChecked()} indeterminate={isAllChecked() && checkedItems.length > 0} />,
      align: 'left',
      padding: 'checkbox'
    },
    'Номер',
    'Статус',
    'Счёт',
    'Счет подрядчика',
    'Текст заявок',
    'Клиентская цена',
    'Базовая цена',
    'Сумма платежа',
    'Дата платежа',
    ''
  ];

  const { showError, showSuccess, showWarning } = useSnack();
  const [wccWarnings, setWccWarnings] = useState<PurchaseWccWarningDto[]>([]);

  const [statusId, setStatus] = useState(0);
  const handleChange = (e: any) => {
    setStatus(e.target.value);
  };

  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (statusId === 0) {
      setDirty(false);
    } else {
      setDirty(true);
    }
  }, [statusId]);

  const submitFormDirty = async (e: any) => {
    if (checkedItems.length === 0) {
      showError('Выберите хотя бы одну заявку');
      return;
    }
    return patchMassPurchaseStatus({ status: Number(statusId), ids: checkedItems })
      .then((r) => {
        setWccWarnings(r.wccWarnings);
        if (r.wccWarnings.length) showWarning('Статусы заявок сохранены. При создании актов возникли ошибки — подробности в списке заявок и аудите.');
        else showSuccess('Статусы заявок успешно изменены');
        setCheckedItems([]);
        collection.reload?.();
      })
      .catch((e) => showError(`Ошибка изменения статуса: ${e}`));
  };

  return (
    <>
      <Box my={1}>
        <PurchasesFilter />
        {wccWarnings.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setWccWarnings([])}>
            {wccWarnings.map((warning) => (
              <Box key={warning.purchaseId}>
                <Link to={`/purchases/${warning.purchaseId}`}>Заявка № {warning.purchaseId}</Link>: {warning.message}
              </Box>
            ))}
          </Alert>
        )}
      </Box>
      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
        <TableCell colSpan={3}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Статус</InputLabel>
            <Select
              style={{ width: '150px' }}
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={statusId}
              label="Статус"
              onChange={handleChange}
            >
              <MenuItem key="" value={0}>
          &nbsp;
              </MenuItem>
              {PURCHASE_STATUSES.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {' '}
                  {s.label}{' '}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>

        <TableCell>
          <Button
            onClick={submitFormDirty}
            variant="contained"
            disabled={!dirty}
            endIcon={<ModeEditIcon />}
          >
      Изменить
          </Button>
        </TableCell>
        <PurchasesTotals
          clientPriceSum={totals?.clientPriceSum ?? 0}
          basePriceSum={totals?.basePriceSum ?? 0}
          payOrderPriceSum={totals?.payOrderPriceSum ?? 0}
        />
      </TableRow>

      <GridLayout
        columns={columns}
        items={collection.collection.items}
        isLoading={collection.isLoading}
        error={collection.error}
        noItemsText="Список заявок пуст"
        pagingData={paging}
        onChangePaging={onChangePaging}
        totalItemsCount={collection.collection.totalItemsCount}
        itemRender={(i) => (
          <PurchaseRow
            key={i.id}
            purchase={i}
            onMenuClick={openMenu}
            checked={checkedItems.some((x) => x === i.id)}
            onCheckChange={(checked) => handleCheckChange(i.id, checked)}
          />
        )}
      />
      <RowMenu menuData={menuData} />
    </>
  );
};

type RowProps = {
  purchase: PurchaseInfoDto;
  onMenuClick: (menuParams: MenuParams<PurchaseInfoDto>) => void;
  checked: boolean;
  onCheckChange: (checked: boolean) => void;
};

export const PurchaseRow = (props: RowProps) => {
  const { purchase, onMenuClick, checked, onCheckChange } = props;
  const status = purchase.status.label;

  const handleMenuClick = (e: MouseEvent<HTMLElement>) => {
    onMenuClick({ anchor: e.currentTarget, target: purchase });
  };

  return (
    <>
      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
        <TableCell padding="checkbox">
          <Checkbox color="primary" checked={checked} onChange={(e, checked) => onCheckChange(checked)} />
        </TableCell>
        <TableCell>
          <LinkToPurchase purchase={purchase} />
        </TableCell>
        <TableCell>
          <Chip label={status} variant="outlined" />
        </TableCell>
        <TableCell>
          <ClientCard invoice={purchase.clientInvoice} invoiceRow={purchase.clientInvoiceRow} />
        </TableCell>
        <TableCell>
          {purchase.supplierInvoice ? (
            <ClientCard invoice={purchase.supplierInvoice} invoiceRow={purchase.supplierInvoiceRow} />
          ) : (
            <InfoBanner message="Счет подрядчика не указан" />
          )}
        </TableCell>
        <TableCell>
          <LegendCard>
            <LegendItem
              title="Продажа:"
              value={
                <>
                  {purchase.clientInvoiceRow.description}
                  {purchase.clientInvoiceRow.media && <LinkToMedia media={purchase.clientInvoiceRow.media}>&nbsp;({purchase.clientInvoiceRow.media.name})</LinkToMedia>}
                </>
              }
            />
            <LegendItem title="Закупка:" value={<>{purchase.supplierInvoiceRow ? purchase.supplierInvoiceRow.description : '-'}</>} />
          </LegendCard>
        </TableCell>
        <TableCell>
          <PriceSpan price={purchase.clientInvoiceRow?.price} />
        </TableCell>
        <TableCell>
          <PriceSpan price={purchase.supplierInvoiceRow?.price ? purchase.supplierInvoiceRow.price : 0} />
        </TableCell>
        <TableCell>
          <PriceSpan price={purchase.paymentAmount ? purchase.paymentAmount : 0} />
        </TableCell>
        <TableCell>
          {formatShortDate(purchase.paymentDate ? purchase.paymentDate : '')}
        </TableCell>
        <TableCell align="right">
          {canDeletePurchase(purchase.status.id) ? (
            <IconButton edge="end" onClick={handleMenuClick}>
              <MoreVertIcon />
            </IconButton>
          ) : null}
        </TableCell>
      </TableRow>
    </>
  );
};

const PurchasesTotals = ({ clientPriceSum, basePriceSum, payOrderPriceSum }: { clientPriceSum: number; basePriceSum: number; payOrderPriceSum: number }) => {
  return (
    <Box px={2} py={1.5}>
      <Stack spacing={0.5} alignItems="flex-start">
        <Box display="flex" alignItems="baseline" gap={1} sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="subtitle1">Сумма по базовой цене:</Typography>
          <PriceSpan price={basePriceSum} size="small" />
        </Box>
        <Box display="flex" alignItems="baseline" gap={1} sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="subtitle1">Сумма по клиентской цене:</Typography>
          <PriceSpan price={clientPriceSum} size="small" />
        </Box>
        <Box display="flex" alignItems="baseline" gap={1} sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="subtitle1">Сумма по платежам:</Typography>
          <PriceSpan price={payOrderPriceSum} size="small" />
        </Box>        
      </Stack>
    </Box>
  );
};

const ClientCard = ({ invoice, invoiceRow }: { invoice: InvoiceDto | null | undefined; invoiceRow: InvoiceRowDto | null | undefined }) => {
  if (!invoice) {
    return <InfoBanner message="Счет не указан" />;
  }
  
  return (
    <Paper sx={{ p: 1 }}>
      <Stack direction="column" spacing={0}>
        <LegendItem title="Счет:" value={<LinkToInvoice invoice={invoice} />} />
        <LegendItem title="Статус:" value={<InvoiceStatusSpan status={invoice.status} size="small" />} />
        <LegendItem title="Сумма:" value={<PriceSpan size="small" price={invoice.totals?.discountPriceTotal || 0} />} />
        {
          invoice.type === INVOICE_TYPE_SUPPLIER && <LegendItem title="Юрлицо продавца:" value={<LinkToLegalEntity legalEntity={invoice.seller} />} />
        }
        
        <LegendItem title="Наше юрлицо:" value={<LinkToLegalEntity legalEntity={
          invoice.type === INVOICE_TYPE_CLIENT ? invoice.seller : invoice.buyer
        } />} />
      </Stack>
    </Paper>
  );
};

const LegendCard = ({ children }: { children: ReactNode }) => {
  return (
    <Paper sx={{ p: 1 }}>
      <Stack direction="column" spacing={0}>
        {children}
      </Stack>
    </Paper>
  );
};
